import { API_CONFIG, API_ENDPOINTS, ML_API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';

// ─── Shared Types (re-exported so SkillsTab can import from one place) ────────

export type SkillType = 'internal' | 'external';
export type Protocol = 'REST' | 'MCP';
export type HttpMethod = 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE';
export type AuthType =
  | 'none'
  | 'api_key'
  | 'oauth2_bearer'
  | 'basic_auth'
  | 'custom_header'
  | 'oauth'
  | 'oauth2'
  | 'basic'
  | 'sigv4'
  | 'oci_signing'
  | 'odoo_session';
export type Transport = 'SSE' | 'Streamable HTTP' | 'stdio';
export type StdioCommand = 'uvx' | 'npx' | 'python';
export type StdioCatalogKey = 'aws' | 'github' | 'oci' | 'custom';

export type EnvVarType = 'fixed' | 'vault' | 'secret' | 'plain';

export interface EnvVarRow {
  key: string;
  value: string;
  isSecret: boolean; // backward compat — new code checks `type` first via envRowIsSecret()
  type?: EnvVarType;
  vault_label?: string;
  readonly?: boolean;
}

/** Returns true if this row holds a sensitive credential (type takes priority over isSecret). */
export function envRowIsSecret(row: EnvVarRow): boolean {
  if (row.type !== undefined) return row.type === 'secret';
  return row.isSecret;
}

/** Template for a pre-configured env row in a catalog entry. */
export interface StdioEnvRowTemplate {
  key: string;
  defaultValue?: string;
  type: EnvVarType;
  vault_label?: string;
}

export interface KnowledgeFile {
  datasource_id: string;
  filename: string;
  tag?: 'knowledge_base' | 'response_template';
}

export interface AuthConfig {
  type: AuthType;
  // api_key
  key_name?: string;
  key_value?: string;
  // oauth / bearer
  access_token?: string;
  refresh_token?: string;
  token_url?: string;
  expires_at?: number;
  // oauth2 client credentials
  client_id?: string;
  client_secret?: string;
  scope?: string;
  audience?: string;
  // basic
  username?: string;
  password?: string;
  // custom_header
  header_name?: string;
  header_value?: string;
  // sigv4 (AWS IAM)
  aws_access_key_id?: string;
  aws_secret_access_key?: string;
  aws_region?: string;
  aws_service?: string;
  // oci_signing
  tenancy_ocid?: string;
  user_ocid?: string;
  fingerprint?: string;
  private_key_pem?: string;
  region?: string;
  // stdio (subprocess transport)
  stdio_command?: StdioCommand;
  stdio_args?: string[];
  // odoo_session
  issue_token_url?: string;
  login?: string;
  db?: string;
  // additional headers sent alongside whichever auth type above is selected
  // id is UI-only (stable React key across add/remove) — never sent to or read from the backend.
  // Any code path sending auth_config to the backend (save, Validate) MUST run it through
  // stripExtraHeaderIds first — see that function's own comment for why.
  extra_headers?: { id?: string; name: string; value: string }[];
}

/**
 * Strip the UI-only `id` from extra_headers rows before auth_config leaves the browser —
 * required on every path that sends it to the backend (skillToApiBody for save, and
 * SkillsTab's handleValidate for Test Connection). Centralized here, rather than inlined
 * at each call site, specifically so the two paths can't drift out of sync again.
 *
 * Deliberately typed concretely against AuthConfig rather than generic — a generic bound
 * to `{ extra_headers?: ... }` infers down to just that constraint (losing every other
 * AuthConfig field) when called with a union like `AuthConfig | { type: 'none' }`, which
 * broke the build. There are exactly two callers and both pass AuthConfig.
 */
export function stripExtraHeaderIds(authConfig: AuthConfig): AuthConfig {
  if (!Array.isArray(authConfig.extra_headers)) return authConfig;
  return {
    ...authConfig,
    extra_headers: authConfig.extra_headers.map(({ name, value }) => ({ name, value })),
  };
}

export interface Skill {
  id: string;
  name: string;
  type: SkillType;
  description: string;
  business_rules: string;
  is_active: boolean;
  is_preloaded: boolean;
  // External common
  protocol?: Protocol;
  auth_config?: AuthConfig;
  router_emails?: string;
  threshold?: number;
  // REST external
  base_url?: string;
  endpoint_path?: string;
  method?: HttpMethod;
  payload_schema?: string;
  response_schema?: string;
  // MCP external
  mcp_server_url?: string;
  transport?: Transport;
  // MCP stdio (subprocess) — form-only fields, flattened into auth_config at save time.
  // stdio_catalog is never sent to the backend; it only drives auto-fill in the UI.
  stdio_catalog?: StdioCatalogKey;
  stdio_command?: StdioCommand;
  stdio_package?: string;
  stdio_version?: string;
  stdio_env_rows?: EnvVarRow[];
  // Knowledge files & tools
  knowledge_files: KnowledgeFile[];
  tool_list: unknown[];
}

export interface StdioCatalogEntry {
  label: string;
  command?: StdioCommand;
  package?: string;
  registry?: 'pypi' | 'npm' | null;
  pypiPackage?: string;
  fallbackVersion?: string;
  /** Simple key list — used when envRows is absent (AWS, custom). */
  envKeys: string[];
  /** Richer row templates — takes priority over envKeys when present (OCI, GitHub). */
  envRows?: StdioEnvRowTemplate[];
  /** Yellow warning callout shown when this catalog entry is selected. */
  warningNote?: string;
  /** OCI IAM policy block shown in the preview panel. */
  ociIamPolicy?: string;
  /** Pre-built business rules template surfaced via the "Pre-fill" button. */
  businessRulesTemplate?: string;
}

const OCI_NSG_BUSINESS_RULES = `You are the OCI Network Operations agent for this channel. You use the OCI MCP tools (get_oci_commands, get_oci_command_help, run_oci_command) strictly for one task: whitelisting a single IP address in a named Network Security Group (NSG) or Security List.

SCOPE
- Task: add an ingress rule for a specific IP/CIDR, port, and protocol.
- Any request outside this: do not attempt it. Route to the team instead.

HARD SAFETY RULES
- NEVER add an ingress rule with source 0.0.0.0/0 (or ::/0). STOP and route.
- NEVER modify a resource tagged/named production without explicit human approval in the request. STOP and route.
- NEVER write to a Security List without reading all existing rules first (see below).
- NEVER remove or modify existing rules unless explicitly asked.
- If a tool call returns a permissions/auth error, STOP and report it.

⚠ CRITICAL — SECURITY LIST WRITE IS A FULL REPLACE
OCI Security Lists have no incremental add endpoint. UpdateSecurityList overwrites the ENTIRE rule set. Always: READ all existing rules → ADD new rule to that list → WRITE the full array back.

FOR NSGs (incremental — safe):
1. Identify NSG OCID/name, IP (/32 default), port, protocol.
2. List existing rules. Stop if already present.
3. Add ingress rule: oci network nsg rules add --nsg-id <ID> --direction INGRESS --protocol tcp --source <CIDR> --source-type CIDR_BLOCK --destination-port-range <port>
4. List rules again to confirm. Report rule added + confirm no 0.0.0.0/0 exists.

FOR SECURITY LISTS (read → modify → write full array):
1. Identify Security List OCID/name, IP, port, protocol.
2. READ full list: oci network security-list get --security-list-id <ID>. Save all existing ingress-security-rules.
3. Check for duplicate. Stop if present.
4. Append the new rule to the saved array in memory.
5. WRITE full array: oci network security-list update --security-list-id <ID> --ingress-security-rules <FULL_ARRAY>
6. Confirm rule count = previous + 1. Report both counts.

OUTPUT: State exact CLI commands run. For Security List updates, always report previous and new rule counts.`;

const OCI_IAM_POLICY = `Allow group NSGWhitelistBots
  to manage security-list
  in compartment prism7:prism7-dev

Allow group NSGWhitelistBots
  to manage security-list
  in compartment prism7:prism7-prod

Allow group NSGWhitelistBots
  to use network-security-groups
  in compartment prism7:prism7-dev`;

/** Known stdio MCP servers — selecting one auto-fills command/package/env keys. Never persisted itself. */
export const MCP_CATALOG: Record<StdioCatalogKey, StdioCatalogEntry> = {
  aws: {
    label: 'AWS (awslabs.aws-api-mcp-server)',
    command: 'uvx',
    package: 'awslabs.aws-api-mcp-server',
    registry: 'pypi',
    pypiPackage: 'awslabs-aws-api-mcp-server',
    fallbackVersion: '1.4.1',
    envKeys: ['AWS_ACCESS_KEY_ID', 'AWS_SECRET_ACCESS_KEY', 'AWS_REGION', 'READ_OPERATIONS_ONLY'],
  },
  github: {
    label: 'GitHub (@github/mcp-server)',
    command: 'npx',
    package: '@github/mcp-server',
    registry: 'npm',
    fallbackVersion: '2.0.0',
    envKeys: [],
    envRows: [
      { key: 'GITHUB_PERSONAL_ACCESS_TOKEN', type: 'secret' },
    ],
  },
  oci: {
    label: 'OCI (oracle.oci-api-mcp-server)',
    command: 'uvx',
    package: 'oracle.oci-api-mcp-server',
    registry: 'pypi',
    pypiPackage: 'oracle-oci-api-mcp-server',
    fallbackVersion: '2.1.4',
    envKeys: [],
    envRows: [
      // User OCID / Tenancy OCID / Fingerprint are identifiers, not secrets — same
      // sensitivity as Region below. They were previously typed 'vault', which renders
      // a vault-secret-LABEL input instead of a value input and always sends a
      // {{vault:<label>}} placeholder string to the backend, never the admin's actual
      // typed value. No vault-secrets-store UI exists anywhere in this app to populate
      // such a label, so those fields could never resolve to real credentials.
      { key: 'OCI_CLI_AUTH',        defaultValue: 'api_key',     type: 'fixed'  },
      { key: 'OCI_CLI_USER',        type: 'plain' },
      { key: 'OCI_CLI_TENANCY',     type: 'plain' },
      { key: 'OCI_CLI_FINGERPRINT', type: 'plain' },
      { key: 'OCI_CLI_KEY_CONTENT', type: 'secret'                                    },
      { key: 'OCI_CLI_REGION',      defaultValue: 'ap-mumbai-1', type: 'plain'  },
      // Value is constrained to true/false/TRUE/FALSE in StdioMcpFields' row renderer
      // (matched by key, not by a new EnvVarType) — flows through the existing generic
      // stdio_env_rows -> env_<KEY> pipeline in skillToApiBody like every other row.
      { key: 'OCI_WRITE_ENABLED',   defaultValue: 'false',       type: 'plain'  },
    ],
    warningNote: "Prototype-only server. Oracle's oci-api-mcp-server is published as a reference/learning tool — not production-grade. Use it to validate the end-to-end flow, then cut over to the Phase B REST + oci_signing skill for production. Always pin a specific version, never @latest.",
    ociIamPolicy: OCI_IAM_POLICY,
    businessRulesTemplate: OCI_NSG_BUSINESS_RULES,
  },
  custom: {
    label: 'Custom stdio',
    registry: null,
    envKeys: [],
  },
};

/** Fetch latest stable version from PyPI/npm for a catalog entry; silent fallback on failure. */
export async function fetchLatestStdioVersion(entry: StdioCatalogEntry): Promise<string | null> {
  try {
    if (entry.registry === 'pypi' && entry.pypiPackage) {
      const res = await fetch(`https://pypi.org/pypi/${entry.pypiPackage}/json`);
      const data = (await res.json()) as { info?: { version?: string } };
      return data?.info?.version ?? entry.fallbackVersion ?? null;
    }
    if (entry.registry === 'npm' && entry.package) {
      const res = await fetch(`https://registry.npmjs.org/${entry.package}/latest`);
      const data = (await res.json()) as { version?: string };
      return data?.version ?? entry.fallbackVersion ?? null;
    }
  } catch {
    return entry.fallbackVersion ?? null;
  }
  return null;
}

/** "package" + "version" → ["package@version"] (or ["package"] if version blank); [] if no package. */
export function buildStdioArgs(pkg?: string, version?: string): string[] {
  const p = (pkg ?? '').trim();
  if (!p) return [];
  const v = (version ?? '').trim();
  return [v ? `${p}@${v}` : p];
}

export interface ValidateResult {
  is_valid: boolean;
  message: string;
  tool_list: unknown[];
  warning?: string;
}

export interface GenerateBusinessRulesRequest {
  email_id: string;
  workspace_name: string;
  categories: string[];
  is_business_rule: boolean;
  skill_name: string;
  skill_description: string;
  channel_id: string;
}

export interface GenerateBusinessRulesResponse {
  status: string;
  message: string;
  descriptions?: string[];
  business_rules?: string[];
}

// ─── Private helpers ──────────────────────────────────────────────────────────

const BASE = API_CONFIG.API_BASE_URL; // https://dev-fourd.shay-ai.com/api/v1

function getBearerToken(): string {
  return (
    localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
    localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
    ''
  );
}

function getAuthHeaders(): Record<string, string> {
  const token = getBearerToken();
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** GET requests — no Content-Type body header (avoids extra CORS preflight). */
function getAuthHeadersForGet(): Record<string, string> {
  const token = getBearerToken();
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  return err instanceof Error && err.name === 'AbortError';
}

function getMultipartAuthHeaders(): Record<string, string> {
  const token = getBearerToken();
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    // No Content-Type — browser sets it with boundary for multipart
  };
}

/**
 * Convert our internal Skill model → API request body.
 *
 * - router_emails: comma-string → array
 * - payload_schema / response_schema: JSON string → object
 * - tool_list for REST: auto-built from path/method/schemas
 * - tool_list for internal: []
 * - tool_list for MCP: whatever was stored from Validate
 */
function skillToApiBody(skill: Skill, channelId: string): Record<string, unknown> {
  let payload_schema: unknown = {};
  let response_schema: unknown = {};
  try {
    if (skill.payload_schema?.trim()) payload_schema = JSON.parse(skill.payload_schema);
  } catch (_) { /* keep {} */ }
  try {
    if (skill.response_schema?.trim()) response_schema = JSON.parse(skill.response_schema);
  } catch (_) { /* keep {} */ }

  const router_emails = skill.router_emails
    ? skill.router_emails.split(',').map((e) => e.trim()).filter(Boolean)
    : [];

  let tool_list: unknown[] = skill.tool_list ?? [];
  if (skill.type === 'external' && skill.protocol === 'REST') {
    tool_list = [
      {
        name: skill.name,
        path: skill.endpoint_path ?? '',
        method: skill.method ?? 'POST',
        parameters: payload_schema,
        response: response_schema,
      },
    ];
  } else if (skill.type === 'internal') {
    tool_list = [];
  }

  const auth_config: Record<string, unknown> = {
    ...stripExtraHeaderIds(skill.auth_config ?? { type: 'none' }),
  };
  if (skill.protocol) auth_config.protocol = skill.protocol;
  if (skill.transport) auth_config.transport = skill.transport;

  if (skill.protocol === 'MCP' && skill.transport === 'stdio') {
    auth_config.stdio_command = skill.stdio_command;
    auth_config.stdio_args = buildStdioArgs(skill.stdio_package, skill.stdio_version);
    for (const row of skill.stdio_env_rows ?? []) {
      const key = row.key.trim();
      // Skip secret rows with empty value (user left blank on edit = "keep existing").
      // Also skip vault rows with empty value as defense-in-depth — vault rows should
      // always carry the {{vault:label}} string after stdioFieldsFromApi, but guard here
      // too so an accidentally-blanked vault row can never overwrite the stored reference.
      if (key && !((envRowIsSecret(row) || row.type === 'vault') && row.value === '')) {
        auth_config[`env_${key}`] = row.value;
      }
    }
  }

  const base_url =
    skill.type === 'external' && skill.protocol === 'MCP'
      ? (skill.transport === 'stdio' ? '' : (skill.mcp_server_url ?? ''))
      : (skill.base_url ?? '');

  return {
    name: skill.name,
    type: skill.type,
    description: skill.description,
    base_url,
    auth_config,
    tool_list,
    payload_schema,
    response_schema,
    business_rules: skill.business_rules,
    knowledge_files: skill.knowledge_files ?? [],
    router_emails,
    threshold: skill.threshold ?? 0,
    plan_versions: {},
    is_active: skill.is_active,
    channel_id: channelId,
  };
}

/** REST skills store path/method on tool_list[0] when top-level fields are absent */
function restFieldsFromToolList(toolList: unknown[]): {
  endpoint_path?: string;
  method?: HttpMethod;
} {
  const first = toolList[0];
  if (!first || typeof first !== 'object') return {};

  const tool = first as {
    path?: string;
    method?: string;
  };

  const path = typeof tool.path === 'string' ? tool.path.trim() : '';
  const method =
    typeof tool.method === 'string' &&
    (['POST', 'GET', 'PUT', 'PATCH', 'DELETE'] as const).includes(tool.method as HttpMethod)
      ? (tool.method as HttpMethod)
      : undefined;

  return {
    ...(path ? { endpoint_path: path } : {}),
    ...(method ? { method } : {}),
  };
}

/** Strip API-only fields (protocol, transport) and ensure required `type`. */
function authConfigFromApi(raw: Record<string, unknown>): AuthConfig {
  return {
    type: (raw.type as AuthType) ?? 'none',
    key_name: raw.key_name as string | undefined,
    key_value: raw.key_value as string | undefined,
    access_token: raw.access_token as string | undefined,
    username: raw.username as string | undefined,
    password: raw.password as string | undefined,
    header_name: raw.header_name as string | undefined,
    header_value: raw.header_value as string | undefined,
    stdio_command: raw.stdio_command as StdioCommand | undefined,
    stdio_args: Array.isArray(raw.stdio_args) ? (raw.stdio_args as string[]) : undefined,
    // odoo_session — password is write-only and intentionally never parsed back from the API
    issue_token_url: raw.issue_token_url as string | undefined,
    login: raw.login as string | undefined,
    db: raw.db as string | undefined,
    // Backfill a stable id per row on load — the backend never stores one, and rows
    // need a React key that survives mid-list deletion (see SkillAuthConfigForm).
    extra_headers: Array.isArray(raw.extra_headers)
      ? (raw.extra_headers as { name: string; value: string }[]).map((h) => ({
          id: crypto.randomUUID(),
          name: h.name,
          value: h.value,
        }))
      : undefined,
  };
}

/** Derive the stdio form-only fields (catalog guess, package/version split, env rows) from a raw auth_config. */
function stdioFieldsFromApi(authConfig: Record<string, unknown>): {
  stdio_catalog: StdioCatalogKey;
  stdio_command?: StdioCommand;
  stdio_package: string;
  stdio_version: string;
  stdio_env_rows: EnvVarRow[];
} {
  const stdio_command = authConfig.stdio_command as StdioCommand | undefined;
  const argsArr = Array.isArray(authConfig.stdio_args) ? (authConfig.stdio_args as string[]) : [];
  const firstArg = argsArr[0] ?? '';
  const atIdx = firstArg.lastIndexOf('@');
  // Scoped npm packages (e.g. "@github/mcp-server") start with '@' — split on the LAST '@' so the
  // version separator is used, not the scope marker.
  const stdio_package = atIdx > 0 ? firstArg.slice(0, atIdx) : firstArg;
  const stdio_version = atIdx > 0 ? firstArg.slice(atIdx + 1) : '';

  const matchedCatalogKey = (Object.keys(MCP_CATALOG) as StdioCatalogKey[]).find(
    (k) => k !== 'custom' && MCP_CATALOG[k].package === stdio_package
  );

  const catalogEntry = matchedCatalogKey ? MCP_CATALOG[matchedCatalogKey] : undefined;

  const stdio_env_rows: EnvVarRow[] = Object.keys(authConfig)
    .filter((k) => k.startsWith('env_'))
    .map((k) => {
      const envKey = k.slice(4);
      const val = String(authConfig[k] ?? '');
      // Infer type from catalog definition first, then from value pattern.
      const catalogRow = catalogEntry?.envRows?.find((r) => r.key === envKey);
      let type: EnvVarType;
      let vault_label: string | undefined;
      if (catalogRow) {
        type = catalogRow.type;
        vault_label = catalogRow.vault_label;
      } else if (val.startsWith('{{vault:')) {
        type = 'vault';
        const m = val.match(/^\{\{vault:([^}]+)\}\}$/);
        vault_label = m?.[1];
      } else if (/token|secret|key|password/i.test(envKey)) {
        type = 'secret';
      } else {
        type = 'plain';
      }
      return {
        key: envKey,
        // Secret values are never re-populated on edit (write-only guarantee).
        // Vault values must round-trip as the {{vault:label}} reference so skillToApiBody
        // can persist them back to the backend unchanged — blanking them (same as secrets)
        // would overwrite the stored vault reference with an empty string on the next Save.
        value: type === 'secret'
          ? ''
          : type === 'vault'
          ? `{{vault:${vault_label ?? envKey.toLowerCase()}}}`
          : val,
        isSecret: type === 'secret',
        type,
        vault_label,
        readonly: type === 'fixed',
      };
    });

  return {
    stdio_catalog: matchedCatalogKey ?? 'custom',
    stdio_command,
    stdio_package,
    stdio_version,
    stdio_env_rows,
  };
}

/**
 * Normalise an API skill response object → internal Skill shape.
 *
 * - router_emails: array | string → comma-string for the form
 * - payload_schema / response_schema: object → pretty-printed JSON string
 * - protocol: may sit at top-level OR inside auth_config
 */
function skillFromApi(raw: Record<string, unknown>): Skill {
  // router_emails
  let router_emails = '';
  if (Array.isArray(raw.router_emails)) {
    router_emails = (raw.router_emails as string[]).join(', ');
  } else if (typeof raw.router_emails === 'string') {
    router_emails = raw.router_emails;
  }

  // schemas
  const payloadRaw = raw.payload_schema;
  const responseRaw = raw.response_schema;
  const payload_schema =
    payloadRaw && typeof payloadRaw === 'object'
      ? JSON.stringify(payloadRaw, null, 2)
      : typeof payloadRaw === 'string'
      ? payloadRaw
      : '';
  const response_schema =
    responseRaw && typeof responseRaw === 'object'
      ? JSON.stringify(responseRaw, null, 2)
      : typeof responseRaw === 'string'
      ? responseRaw
      : '';

  // protocol (top-level takes priority; fallback to auth_config.protocol per API docs)
  const authConfig = (raw.auth_config as Record<string, unknown> | undefined) ?? {};
  const protocol =
    (raw.protocol as Protocol | undefined) ??
    (authConfig.protocol as Protocol | undefined);

  const baseUrl = raw.base_url as string | undefined;
  const mcpServerUrl =
    (raw.mcp_server_url as string | undefined) ??
    (protocol === 'MCP' ? baseUrl : undefined);

  const tool_list = Array.isArray(raw.tool_list) ? raw.tool_list : [];
  const fromTools = restFieldsFromToolList(tool_list);

  const endpoint_path =
    (typeof raw.endpoint_path === 'string' && raw.endpoint_path.trim()
      ? raw.endpoint_path
      : undefined) ?? fromTools.endpoint_path;

  const method =
    (raw.method as HttpMethod | undefined) ?? fromTools.method;

  const transport =
    (raw.transport as Transport | undefined) ??
    (authConfig.transport as Transport | undefined);

  const stdioFields = transport === 'stdio' ? stdioFieldsFromApi(authConfig) : undefined;

  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    type: (raw.type as SkillType) ?? 'external',
    description: String(raw.description ?? ''),
    business_rules: String(raw.business_rules ?? ''),
    is_active: Boolean(raw.is_active),
    is_preloaded: Boolean(raw.is_preloaded),
    protocol,
    auth_config: authConfigFromApi(authConfig),
    base_url: baseUrl,
    endpoint_path,
    method,
    payload_schema,
    response_schema,
    mcp_server_url: mcpServerUrl,
    transport,
    ...(stdioFields ?? {}),
    router_emails,
    threshold: typeof raw.threshold === 'number' ? raw.threshold : 0,
    knowledge_files: Array.isArray(raw.knowledge_files)
      ? (raw.knowledge_files as KnowledgeFile[])
      : [],
    tool_list,
  };
}

// ─── Public API functions ─────────────────────────────────────────────────────

/** In-flight dedupe: React Strict Mode remounts must not fire duplicate GETs for the same channel. */
const skillsListInFlight = new Map<string, Promise<Skill[]>>();

async function fetchSkillsFromApi(channelId: string, signal?: AbortSignal): Promise<Skill[]> {
  const url = `${BASE}${API_ENDPOINTS.SKILLS.LIST(channelId)}`;
  const requestInit: RequestInit = {
    method: 'GET',
    mode: 'cors',
    cache: 'no-cache',
    headers: getAuthHeadersForGet(),
    redirect: 'follow',
  };
  if (signal) requestInit.signal = signal;

  const request = new Request(url, requestInit);
  let res: Response;
  try {
    res = await fetch(request);
  } catch (err) {
    if (isAbortError(err)) throw err;
    throw new Error(
      'Unable to reach the skills API. Check your network connection and try again.'
    );
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Failed to load skills (${res.status})`);
  }
  const data = await res.json();
  const arr: Record<string, unknown>[] = Array.isArray(data)
    ? data
    : (data.skills ?? data.data ?? []);
  return arr.map(skillFromApi);
}

/** GET /api/v1/skills/?channel_id={channelId} */
export async function fetchSkills(
  channelId: string,
  options?: { signal?: AbortSignal; force?: boolean }
): Promise<Skill[]> {
  const signal = options?.signal;
  const force = options?.force ?? false;

  if (!force) {
    const existing = skillsListInFlight.get(channelId);
    if (existing) return existing;
  }

  const request = fetchSkillsFromApi(channelId, signal).finally(() => {
    skillsListInFlight.delete(channelId);
  });

  skillsListInFlight.set(channelId, request);
  return request;
}

/** POST /api/v1/skills/ — trailing slash avoids 307 redirect (4 network calls → 2). */
export async function createSkill(skill: Skill, channelId: string): Promise<Skill> {
  const url = `${BASE}${API_ENDPOINTS.SKILLS.CREATE}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      redirect: 'follow',
      headers: getAuthHeaders(),
      body: JSON.stringify(skillToApiBody(skill, channelId)),
    });
  } catch (err) {
    if (isAbortError(err)) throw err;
    throw new Error(
      'Unable to reach the skills API. Check your network connection and try again.'
    );
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Failed to create skill (${res.status})`);
  }
  return skillFromApi(await res.json());
}

/** PUT /api/v1/skills/{skillId} */
export async function updateSkill(skill: Skill, channelId: string): Promise<Skill> {
  const res = await fetch(`${BASE}${API_ENDPOINTS.SKILLS.UPDATE(skill.id)}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(skillToApiBody(skill, channelId)),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to update skill (${res.status})`);
  }
  return skillFromApi(await res.json());
}

/** PATCH /api/v1/skills/{skillId}/toggle */
export async function toggleSkillActive(skillId: string, isActive: boolean): Promise<void> {
  const res = await fetch(`${BASE}${API_ENDPOINTS.SKILLS.TOGGLE(skillId)}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ is_active: isActive }),
  });
  if (!res.ok) throw new Error(`Failed to toggle skill (${res.status})`);
}

/**
 * DELETE skill — first tries to delete all attached knowledge-file datasources
 * (failures are non-fatal per spec), then deletes the skill itself.
 */
export async function deleteSkill(skill: Skill): Promise<void> {
  // Step 1 — delete datasources (non-fatal)
  await Promise.allSettled(
    skill.knowledge_files.map((kf) =>
      fetch(`${BASE}${API_ENDPOINTS.DATASOURCES.DELETE(kf.datasource_id)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })
    )
  );
  // Step 2 — delete skill
  const res = await fetch(`${BASE}${API_ENDPOINTS.SKILLS.DELETE(skill.id)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to delete skill (${res.status})`);
}

/** POST /api/v1/skills/validate — REST */
export async function validateRestSkill(
  base_url: string,
  auth_config: AuthConfig
): Promise<ValidateResult> {
  const res = await fetch(`${BASE}${API_ENDPOINTS.SKILLS.VALIDATE}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ base_url, auth_config }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Validation failed (${res.status})`);
  }
  return res.json();
}

/** POST /shay/email/generate-description — business rules from name + description */
export async function generateBusinessRules(
  payload: GenerateBusinessRulesRequest,
  signal?: AbortSignal
): Promise<GenerateBusinessRulesResponse> {
  const res = await fetch(`${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.GENERATE_DESCRIPTION}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
    signal,
  });

  if (!res.ok) {
    throw new Error(await parseApiErrorMessage(res));
  }

  return res.json() as Promise<GenerateBusinessRulesResponse>;
}

async function parseApiErrorMessage(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const body = JSON.parse(text) as { detail?: string | Array<{ msg?: string }>; message?: string };
    if (typeof body.detail === 'string') return body.detail;
    if (Array.isArray(body.detail)) {
      const parts = body.detail.map((d) => d.msg).filter(Boolean);
      if (parts.length) return parts.join(', ');
    }
    if (body.message) return body.message;
  } catch (_) {
    /* use raw text */
  }
  return text || `Validation failed (${res.status})`;
}

// Intentionally minimal — only the two headers that would silently break auth if
// overridden. Not an exhaustive hop-by-hop/sensitive-header blocklist (e.g. cookie,
// host); this is an admin-level form, not a public-input surface.
const RESERVED_HEADER_NAMES = new Set(['authorization', 'content-type']);

function authConfigToMcpHeaders(authConfig?: AuthConfig): Record<string, string> {
  if (!authConfig || authConfig.type === 'none') return {};

  // odoo_session intentionally falls through to the default `{}` case below — the token exchange
  // needs the Odoo login+password, which must never be sent from the browser (CORS + credential
  // exposure risk, see docs/dev-tasks/odoo-mcp-skill-auth-integration.md Section 3). Test Connection
  // for this type is routed to a backend-proxied validate call instead of this client-side builder.
  let headers: Record<string, string>;
  switch (authConfig.type) {
    case 'api_key':
      headers = authConfig.key_name && authConfig.key_value
        ? { [authConfig.key_name]: authConfig.key_value }
        : {};
      break;
    case 'oauth2_bearer':
      headers = authConfig.access_token ? { Authorization: `Bearer ${authConfig.access_token}` } : {};
      break;
    case 'basic_auth':
      if (authConfig.username && authConfig.password) {
        const encoded =
          typeof btoa === 'function'
            ? btoa(`${authConfig.username}:${authConfig.password}`)
            : '';
        headers = encoded ? { Authorization: `Basic ${encoded}` } : {};
      } else {
        headers = {};
      }
      break;
    case 'custom_header':
      headers = authConfig.header_name && authConfig.header_value
        ? { [authConfig.header_name]: authConfig.header_value }
        : {};
      break;
    default:
      headers = {};
  }

  // Reserved so an extra header can't silently clobber the primary auth header via a
  // typo or a malicious "Authorization" row — extra_headers is additive, not an override.
  for (const h of authConfig.extra_headers ?? []) {
    if (h.name && h.value && !RESERVED_HEADER_NAMES.has(h.name.toLowerCase())) {
      headers[h.name] = h.value;
    }
  }
  return headers;
}

function buildMcpRequestHeaders(
  authConfig?: AuthConfig,
  sessionId?: string
): Record<string, string> {
  const token = getBearerToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json, text/event-stream',
    ...authConfigToMcpHeaders(authConfig),
  };
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (sessionId) {
    headers['Mcp-Session-Id'] = sessionId;
  }
  return headers;
}

function readMcpSessionId(res: Response, fallback?: string): string | undefined {
  return (
    res.headers.get('mcp-session-id') ??
    res.headers.get('Mcp-Session-Id') ??
    fallback
  ) ?? undefined;
}

async function parseMcpJsonRpcBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text.trim()) return null;

  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('text/html') || text.trimStart().startsWith('<!')) {
    return { __html: true };
  }

  if (contentType.includes('application/json') || text.trimStart().startsWith('{')) {
    try {
      return JSON.parse(text);
    } catch (_) {
      return null;
    }
  }

  if (contentType.includes('text/event-stream') || text.includes('data:')) {
    let last: unknown = null;
    for (const line of text.split('\n')) {
      if (!line.startsWith('data:')) continue;
      const payload = line.slice(5).trim();
      if (!payload || payload === '[DONE]') continue;
      try {
        last = JSON.parse(payload);
      } catch (_) {
        /* skip partial SSE chunk */
      }
    }
    return last;
  }

  try {
    return JSON.parse(text);
  } catch (_) {
    return null;
  }
}

function extractMcpTools(data: unknown): unknown[] {
  if (!data || typeof data !== 'object') return [];
  const record = data as { result?: { tools?: unknown[] }; error?: { message?: string } };
  if (record.error) return [];
  return Array.isArray(record.result?.tools) ? record.result.tools : [];
}

function extractMcpError(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const err = (data as { error?: { message?: string } }).error;
  return err?.message ?? null;
}

async function mcpJsonRpcRequest(
  url: string,
  method: string,
  params: Record<string, unknown>,
  options: {
    id?: number;
    notification?: boolean;
    sessionId?: string;
    authConfig?: AuthConfig;
  } = {}
): Promise<{ response: Response; data: unknown; sessionId?: string }> {
  const body: Record<string, unknown> = { jsonrpc: '2.0', method, params };
  if (!options.notification) body.id = options.id ?? 1;

  const response = await fetch(url, {
    method: 'POST',
    headers: buildMcpRequestHeaders(options.authConfig, options.sessionId),
    body: JSON.stringify(body),
  });

  const data = await parseMcpJsonRpcBody(response);
  const sessionId = readMcpSessionId(response, options.sessionId);
  return { response, data, sessionId };
}

/**
 * Resolve MCP URL for Validate only. Relative paths (e.g. `/mcp`) otherwise hit
 * the Vite dev origin (localhost:3000); map those and local dev URLs to API host.
 * Host `/mcp` serves the SPA — MCP JSON-RPC uses `/api/v1/mcp`.
 */
function resolveMcpValidateUrl(mcpServerUrl: string): string {
  const trimmed = mcpServerUrl.trim();
  if (!trimmed) return trimmed;

  const apiOrigin = API_CONFIG.ML_API_BASE_URL.replace(/\/$/, '');

  let resolved: string;
  if (trimmed.startsWith('/')) {
    resolved = `${apiOrigin}${trimmed}`;
  } else if (!/^https?:\/\//i.test(trimmed)) {
    resolved = `${apiOrigin}/${trimmed.replace(/^\//, '')}`;
  } else {
    try {
      const url = new URL(trimmed);
      if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
        resolved = `${apiOrigin}${url.pathname}${url.search}${url.hash}`;
      } else {
        resolved = trimmed;
      }
    } catch (_) {
      return trimmed;
    }
  }

  try {
    const u = new URL(resolved);
    const apiHost = new URL(apiOrigin).host;
    if (u.host === apiHost && (u.pathname === '/mcp' || u.pathname === '/mcp/')) {
      return `${apiOrigin}/api/v1/mcp`;
    }
  } catch (_) {
    /* keep resolved */
  }

  return resolved;
}

/** Server-side MCP validation via POST /api/v1/skills/validate */
async function validateMcpSkillViaApi(
  validateUrl: string,
  authConfig?: AuthConfig
): Promise<ValidateResult | null> {
  const auth_config = {
    ...(authConfig ?? { type: 'none' as const }),
    protocol: 'MCP',
  };

  const res = await fetch(`${BASE}${API_ENDPOINTS.SKILLS.VALIDATE}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      protocol: 'MCP',
      base_url: validateUrl,
      mcp_server_url: validateUrl,
      auth_config,
    }),
  });

  if (res.status === 404 || res.status === 405) return null;

  if (!res.ok) {
    if (res.status === 422 || res.status === 400) return null;
    throw new Error(await parseApiErrorMessage(res));
  }

  const data = (await res.json()) as ValidateResult;
  const tools = data.tool_list ?? [];
  return {
    is_valid: data.is_valid !== false,
    message: data.message || `${tools.length} tool(s) discovered.`,
    tool_list: tools,
    warning: data.warning,
  };
}

/** Direct MCP JSON-RPC: initialize → notifications/initialized → tools/list */
async function validateMcpSkillDirect(
  validateUrl: string,
  authConfig?: AuthConfig
): Promise<ValidateResult> {
  const protocolVersions = ['2025-03-26', '2024-11-05'];

  for (const protocolVersion of protocolVersions) {
    let sessionId: string | undefined;

    try {
      const init = await mcpJsonRpcRequest(
        validateUrl,
        'initialize',
        {
          protocolVersion,
          capabilities: {},
          clientInfo: { name: 'shay-skill-admin', version: '1.0' },
        },
        { id: 1, authConfig }
      );

      if ((init.data as { __html?: boolean })?.__html) {
        return {
          is_valid: false,
          message:
            'Invalid MCP Server URL — this path returns a web page, not an MCP endpoint. Use a full MCP JSON-RPC URL.',
          tool_list: [],
        };
      }

      const initError = extractMcpError(init.data);
      if (!init.response.ok || initError) {
        continue;
      }

      sessionId = init.sessionId;

      await mcpJsonRpcRequest(
        validateUrl,
        'notifications/initialized',
        {},
        { notification: true, sessionId, authConfig }
      );

      const toolsCall = await mcpJsonRpcRequest(
        validateUrl,
        'tools/list',
        {},
        { id: 2, sessionId, authConfig }
      );

      if ((toolsCall.data as { __html?: boolean })?.__html) {
        return {
          is_valid: false,
          message:
            'Invalid MCP Server URL — this path returns a web page, not an MCP endpoint. Use a full MCP JSON-RPC URL.',
          tool_list: [],
        };
      }

      const toolsError = extractMcpError(toolsCall.data);
      if (!toolsCall.response.ok || toolsError) {
        const detail = toolsError || `HTTP ${toolsCall.response.status}`;
        return {
          is_valid: false,
          message: `Could not retrieve tool list: ${detail}`,
          tool_list: [],
        };
      }

      const tools = extractMcpTools(toolsCall.data);
      return {
        is_valid: true,
        message: `${tools.length} tool(s) discovered.`,
        tool_list: tools,
      };
    } catch (err) {
      if (protocolVersion === protocolVersions[protocolVersions.length - 1]) {
        const msg = err instanceof Error ? err.message : 'Validation failed';
        if (/failed to fetch|network/i.test(msg)) {
          return {
            is_valid: true,
            message: 'SSE server requires a live EventSource — skill will still work at runtime.',
            tool_list: [],
            warning: 'SSE server requires a live EventSource — skill will still work at runtime.',
          };
        }
        return { is_valid: false, message: msg, tool_list: [] };
      }
    }
  }

  return {
    is_valid: false,
    message: 'Could not connect to MCP server. Check the URL and authentication.',
    tool_list: [],
  };
}

/**
 * MCP validation — prefers POST /api/v1/skills/validate, then direct JSON-RPC handshake.
 */
export async function validateMcpSkill(
  mcpServerUrl: string,
  authConfig?: AuthConfig
): Promise<ValidateResult> {
  const validateUrl = resolveMcpValidateUrl(mcpServerUrl);
  if (!validateUrl) {
    return { is_valid: false, message: 'MCP Server URL is required.', tool_list: [] };
  }

  try {
    const apiResult = await validateMcpSkillViaApi(validateUrl, authConfig);
    if (apiResult) return apiResult;
  } catch (err) {
    if (err instanceof Error) throw err;
  }

  return validateMcpSkillDirect(validateUrl, authConfig);
}

export interface ValidateStdioResult {
  status: 'ok' | 'error';
  message?: string;
  tools?: string[];
  latency_ms?: number;
}

/**
 * POST /shay/email/validate-skill-connection — stdio transport only.
 * Spins up the subprocess (~1-3s), lists its tools, and tears it down.
 */
export async function validateSkillConnection(
  authConfig: Record<string, unknown>
): Promise<ValidateStdioResult> {
  const res = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.VALIDATE_SKILL_CONNECTION}`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ auth_config: authConfig }),
    }
  );

  if (!res.ok) {
    return { status: 'error', message: await parseApiErrorMessage(res) };
  }

  return res.json() as Promise<ValidateStdioResult>;
}

function formatSchemaField(schema: unknown): string {
  if (schema == null) return '';
  if (typeof schema === 'string') return schema;
  return JSON.stringify(schema, null, 2);
}

export interface DiscoverSchemaResult {
  payload_schema: string;
  response_schema: string;
  message?: string;
  encoding?: string;
}

/** POST /api/v1/skills/discover-schema — auto-fill payload/response from OpenAPI */
export async function discoverSchema(
  base_url: string,
  endpoint_path: string,
  method: string,
  auth_config: AuthConfig
): Promise<DiscoverSchemaResult> {
  const res = await fetch(`${BASE}${API_ENDPOINTS.SKILLS.DISCOVER_SCHEMA}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      base_url,
      endpoint_path,
      method,
      auth_config: auth_config ?? { type: 'none' },
    }),
  });

  if (!res.ok) {
    throw new Error(await parseApiErrorMessage(res));
  }

  const data = (await res.json()) as {
    message?: string;
    encoding?: string;
    payload_schema?: unknown;
    response_schema?: unknown;
  };

  return {
    message: data.message,
    encoding: data.encoding,
    payload_schema: formatSchemaField(data.payload_schema),
    response_schema: formatSchemaField(data.response_schema),
  };
}

// ─── Knowledge File helpers ───────────────────────────────────────────────────

interface UploadedFileInfo {
  file_path: string;
  filename: string;
  size: number;
  type: string;
}

/** Step 1 — POST /api/v1/datasources/upload/bulk */
async function uploadFilesStep1(files: File[]): Promise<UploadedFileInfo[]> {
  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));
  formData.append('isValidationRequired', 'false');

  const res = await fetch(`${BASE}${API_ENDPOINTS.DATASOURCES.UPLOAD_BULK}`, {
    method: 'POST',
    headers: getMultipartAuthHeaders(),
    body: formData,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `File upload failed (${res.status})`);
  }
  const data = await res.json() as Record<string, unknown>;
  const uploadedFiles: Record<string, unknown>[] = Array.isArray(data.uploaded_files)
    ? (data.uploaded_files as Record<string, unknown>[])
    : Array.isArray(data)
      ? (data as Record<string, unknown>[])
      : ((data.files ?? data.data ?? []) as Record<string, unknown>[]);

  return files.map((f, i) => {
    const entry = uploadedFiles[i] ?? {};
    return {
      file_path: String(entry.storage_path ?? entry.file_path ?? entry.path ?? ''),
      filename: String(entry.filename ?? entry.original_filename ?? f.name),
      size: Number(entry.size ?? entry.file_size ?? f.size),
      type: String(entry.file_type ?? entry.type ?? (f.type || 'application/octet-stream')),
    };
  });
}

function extractCreatedDatasourceRecords(data: Record<string, unknown>): Record<string, unknown>[] {
  const candidates = [
    data.created_datasources,
    data.created,
    data.datasources,
    data.data,
    data.results,
    data.successful_datasources,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate as Record<string, unknown>[];
    }
  }
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  return [];
}

function mapRecordsToKnowledgeFiles(
  records: Record<string, unknown>[],
  uploads: UploadedFileInfo[]
): KnowledgeFile[] {
  return records
    .map((d, i) => ({
      datasource_id: String(d.id ?? d.datasource_id ?? d._id ?? ''),
      filename: String(d.filename ?? d.name ?? uploads[i]?.filename ?? ''),
    }))
    .filter((kf) => kf.filename);
}

/** Resolve datasource ids from channel list when bulk response only returns counts */
export async function fetchKnowledgeFilesByFilenames(
  channelId: string,
  filenames: string[]
): Promise<KnowledgeFile[]> {
  if (!filenames.length) return [];

  const params = new URLSearchParams({
    page: '1',
    size: '100',
    channel_id: channelId,
  });

  const res = await fetch(`${BASE}${API_ENDPOINTS.DATASOURCES.LIST}?${params}`, {
    method: 'GET',
    headers: getAuthHeadersForGet(),
  });
  if (!res.ok) return [];

  const body = await res.json() as {
    datasources?: Array<Record<string, unknown>>;
    data?: Array<Record<string, unknown>>;
  };
  const list = body.datasources ?? body.data ?? [];
  const wanted = new Set(filenames.map((n) => n.toLowerCase()));

  return list
    .filter((ds) => {
      const name = String(ds.filename ?? ds.name ?? '').toLowerCase();
      return name && wanted.has(name);
    })
    .map((ds) => ({
      datasource_id: String(ds.id ?? ds.datasource_id ?? ''),
      filename: String(ds.filename ?? ds.name ?? ''),
    }))
    .filter((kf) => kf.datasource_id && kf.filename);
}

/** Step 2 — POST /api/v1/datasources/bulk */
async function registerDatasourcesStep2(
  channelId: string,
  uploads: UploadedFileInfo[]
): Promise<KnowledgeFile[]> {
  const res = await fetch(`${BASE}${API_ENDPOINTS.DATASOURCES.CREATE_BULK}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      datasources: uploads.map((u) => ({
        storage_type: 'local',
        channel_id: channelId,
        filename: u.filename,
        file_size: u.size,
        file_type: u.type,
        is_embedding_required: true,
        datasource_metadata: { datasource_type: 'skill' },
        config: { file_path: u.file_path },
      })),
    }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Datasource registration failed (${res.status})`);
  }

  const data = await res.json() as Record<string, unknown>;
  const totalFailed = Number(data.total_failed ?? 0);
  const totalCreated = Number(data.total_created ?? 0);

  if (totalFailed > 0 && totalCreated === 0) {
    const failed = data.failed_datasources as Array<{ error?: string }> | undefined;
    throw new Error(failed?.[0]?.error ?? 'Datasource registration failed.');
  }

  const records = extractCreatedDatasourceRecords(data);
  let knowledgeFiles = mapRecordsToKnowledgeFiles(records, uploads).filter((kf) => kf.datasource_id);

  if (knowledgeFiles.length === 0 && totalCreated > 0) {
    knowledgeFiles = await fetchKnowledgeFilesByFilenames(
      channelId,
      uploads.map((u) => u.filename)
    );
  }

  if (knowledgeFiles.length === 0 && totalCreated > 0) {
    knowledgeFiles = uploads.map((u) => ({ datasource_id: '', filename: u.filename }));
  }

  return knowledgeFiles;
}

/** Full 2-step upload + embed for knowledge files */
export async function uploadKnowledgeFiles(
  files: File[],
  channelId: string,
  tag?: 'knowledge_base' | 'response_template'
): Promise<KnowledgeFile[]> {
  const uploads = await uploadFilesStep1(files);
  const knowledgeFiles = await registerDatasourcesStep2(channelId, uploads);
  return tag ? knowledgeFiles.map((kf) => ({ ...kf, tag })) : knowledgeFiles;
}

/** DELETE /api/v1/datasources/{datasource_id} — no request body */
export async function deleteDatasource(datasourceId: string): Promise<void> {
  if (!datasourceId) return;

  const res = await fetch(`${BASE}${API_ENDPOINTS.DATASOURCES.DELETE(datasourceId)}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    throw new Error(await parseApiErrorMessage(res));
  }
}

/**
 * Remove a single knowledge file from a saved skill:
 *  Step 1 — DELETE the datasource
 *  Step 2 — PATCH the skill's knowledge_files list
 */
export async function removeKnowledgeFile(
  skillId: string,
  datasourceId: string,
  remainingFiles: KnowledgeFile[]
): Promise<void> {
  await deleteDatasource(datasourceId);

  const res = await fetch(`${BASE}${API_ENDPOINTS.SKILLS.PATCH(skillId)}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ knowledge_files: remainingFiles }),
  });
  if (!res.ok) throw new Error(`Failed to update skill after file removal (${res.status})`);
}

// ─── Skill-datasource registry ────────────────────────────────────────────────
// Module-level Set of datasource IDs that belong to skills.
// Written by SkillsTab immediately after upload linking (before the skill is saved),
// so DataSourcesPanel can exclude them synchronously without waiting for the list API.
const _skillLinkedDatasourceIds = new Set<string>();

/** Register datasource IDs as skill-owned so they are excluded from the channel panel. */
export function claimSkillDatasourceIds(ids: string[]): void {
  ids.forEach((id) => { if (id) _skillLinkedDatasourceIds.add(id); });
}

/** Read the current registry — called synchronously during render by DataSourcesPanel. */
export function getSkillLinkedDatasourceIds(): ReadonlySet<string> {
  return _skillLinkedDatasourceIds;
}